import React from "react";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import Head from "next/head";
import ModelSearchPageV2 from "../components/modals/ModelSearchPageV2";
import connectToDb from "../utils/mongodb";
import { MODEL_PROJECTION } from "./api/model";

interface ModelsPageProps {
  newParam: string | null;
  filter: string | null;
  // Slug of the live /ai-voices/[slug] hub for `filter`, when there is one.
  filterHub?: string | null;
  isSearch?: boolean;
  initialModels: any[] | null;
  initialCollections: any[] | null;
  initialBadges: any[] | null;
}

const ModelsPage = (props: ModelsPageProps) => {
  const filterTitle = props.filter
    ? ` - ${props.filter.charAt(0).toUpperCase() + props.filter.slice(1)}`
    : "";

  // Filtered library views canonicalize to their /ai-voices/[slug] hub so they
  // don't compete with the SEO category pages for the same terms. Only when
  // that hub exists: /models?filter=<anything> used to canonicalize to an
  // /ai-voices URL that 404s (or a <25-voice hub that is noindex).
  // Everything else is a view of /models and canonicalizes to it; searches
  // (?q=, ?search=) and hubless filters are also noindex, so they cannot mint
  // an unbounded set of indexable URLs. ?sort= etc. rely on the canonical.
  const pageCanonical = props.filterHub
    ? `https://www.jammable.com/ai-voices/${props.filterHub}`
    : "https://www.jammable.com/models";
  const noindex = Boolean(props.isSearch || (props.filter && !props.filterHub));

  const breadcrumbItems = [
    {
      "@type": "ListItem" as const,
      position: 1,
      name: "Home",
      item: "https://www.jammable.com",
    },
    {
      "@type": "ListItem" as const,
      position: 2,
      name: "AI Voice Library",
      item: "https://www.jammable.com/models",
    },
  ];

  if (props.filter && props.filterHub) {
    breadcrumbItems.push({
      "@type": "ListItem" as const,
      position: 3,
      name: `${props.filter.charAt(0).toUpperCase() + props.filter.slice(1)} Voices`,
      item: pageCanonical,
    });
  }

  return (
    <>
      <NextSeo
        title={`AI Voice Library${filterTitle} | Jammable`}
        description="Browse 93,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more. New voices added daily."
        canonical={pageCanonical}
        noindex={noindex}
        openGraph={{
          title: `AI Voice Library${filterTitle} | Jammable`,
          description:
            "Browse 93,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more.",
          url: pageCanonical,
          type: "website",
          locale: "en_US",
          siteName: "Jammable",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: breadcrumbItems,
            }),
          }}
        />
      </Head>
      <ModelSearchPageV2
        initialModels={props.initialModels}
        initialCollections={props.initialCollections}
        initialBadges={props.initialBadges}
        noindex={noindex}
      />
    </>
  );
};

export default ModelsPage;

export const getServerSideProps: GetServerSideProps = async ({
  query,
  res,
}) => {
  const Model = (await import("../models/Model")).default;
  const Collection = (await import("../models/Collection")).default;
  const filter = (query?.filter as string) || null;
  // ?search= isn't read by the page, but crawlers find it linked; treat it as
  // a search so it can't become an indexable copy of /models either.
  const isSearch = Boolean(query?.q || query?.search);
  // The category pills, from the same cache /api/model/getBadges reads, so
  // they're in the page rather than popping in after it loads.
  const initialBadges = await (async () => {
    try {
      const { getRedisClient } = await import("../utils/redisClient");
      const redis = await getRedisClient();
      const cached = await redis?.get("badges:stats");
      return cached ? JSON.parse(cached).data ?? null : null;
    } catch {
      return null;
    }
  })();
  // Same cache getHubCategories reads. 25: the noindex threshold in
  // components/hub/CategoryHub.tsx.
  const filterHub = await (async () => {
    if (!filter || !Array.isArray(initialBadges)) return null;
    const { slugifyBadge } = await import("../utils/categoryHubs");
    const slug = slugifyBadge(filter);
    const hub = initialBadges.some(
      (b: { badge?: string; count?: number }) =>
        b.badge && (b.count || 0) >= 25 && slugifyBadge(b.badge) === slug,
    );
    return hub ? slug : null;
  })();

  // Only SSR the initial load (no search query, default sort)
  // Search queries fall back to CSR since they need Atlas Search
  const search = (query?.q as string) || "";
  if (search) {
    return {
      props: {
        newParam: query.new ?? null,
        filter,
        filterHub,
        isSearch,
        initialModels: null,
        initialCollections: null,
        initialBadges,
      },
    };
  }

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=3600, stale-while-revalidate=86400",
  );

  try {
    await connectToDb();

    const matchStage: Record<string, any> = {
      training: false,
      isPublic: true,
      private: false,
      isRemoved: { $ne: true },
      deleted: { $ne: true },
    };

    if (filter) {
      matchStage.$or = [{ badges: filter }, { classification: filter }];
    }

    const [models, collections] = await Promise.all([
      Model.aggregate([
        { $match: matchStage },
        { $sort: { usesLast24Hours: -1, uses: -1, _id: 1 } },
        { $limit: 25 },
        { $project: MODEL_PROJECTION },
      ]),
      Collection.aggregate([
        {
          $match: {
            modelsCount: { $gte: 3 },
            isPrivate: { $ne: true },
            deleted: { $ne: true },
          },
        },
        { $sort: { views: -1, likes: -1, modelsCount: -1, _id: 1 } },
        { $limit: 10 },
        { $project: MODEL_PROJECTION },
      ]),
    ]);

    return {
      props: {
        newParam: query.new ?? null,
        filter,
        filterHub,
        isSearch,
        initialModels: JSON.parse(JSON.stringify(models)),
        initialCollections: JSON.parse(JSON.stringify(collections)),
        initialBadges,
      },
    };
  } catch (error) {
    console.error("Error fetching initial models:", error);
    return {
      props: {
        newParam: query.new ?? null,
        filter,
        filterHub,
        isSearch,
        initialModels: null,
        initialCollections: null,
        initialBadges,
      },
    };
  }
};
